import React from 'react';
import { cn } from '@/lib/utils';

export type TabVariant = 'primary' | 'subtle' | 'underline';

export interface TabProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  active?: boolean;
  variant?: TabVariant;
}

export const Tab = React.forwardRef<HTMLButtonElement, TabProps>(
  ({ className, children, active = false, variant = 'primary', disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          variant === 'underline'
            ? 'relative shrink-0 px-4 pt-1 pb-3 text-[13px] font-semibold tracking-[0.01em] transition-colors'
            : 'rounded-md px-4 py-2 text-sm font-medium transition-colors',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          variant === 'primary' &&
            (active
              ? 'bg-primary text-primary-foreground'
              : 'bg-card text-muted hover:text-foreground hover:bg-surface'),
          variant === 'subtle' &&
            (active
              ? 'bg-card text-foreground border-border border shadow-sm'
              : 'text-muted hover:text-foreground'),
          variant === 'underline' &&
            (active
              ? 'text-foreground after:bg-primary after:absolute after:right-3 after:bottom-[-1px] after:left-3 after:h-[3px] after:rounded-t-full'
              : 'text-muted hover:text-foreground'),
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  },
);
Tab.displayName = 'Tab';

export interface TabGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  variant?: TabVariant;
}

export const TabGroup = React.forwardRef<HTMLDivElement, TabGroupProps>(
  ({ className, children, variant = 'primary', ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          variant === 'underline'
            ? 'border-border/70 flex items-end gap-1 overflow-x-auto border-b'
            : 'flex space-x-1',
          variant === 'subtle' && 'bg-surface rounded-lg p-1',
          className,
        )}
        {...props}
      >
        {React.Children.map(children, (child) => {
          if (!React.isValidElement(child)) return child;
          if (child.type === Tab) {
            return React.cloneElement(child as React.ReactElement<TabProps>, {
              variant: (child.props as TabProps).variant ?? variant,
            });
          }
          return child;
        })}
      </div>
    );
  },
);
TabGroup.displayName = 'TabGroup';
